import { QueryResult } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { GetTenantStoresQuery } from 'graphql/generated-types'
import { useMemo } from 'react'

import useAuth from '@/hooks/useAuth'
import useLoggedInUser from '@/hooks/useLoggedInUser'

import { AccountSetUpParams, useAccountSetUpFlow } from './accountSetUpConfig'
import { UserType } from './types'

interface UseAccountSetUpProps {
  loggedInUser: ReturnType<typeof useLoggedInUser>
  auth: ReturnType<typeof useAuth>
  tenantStores: QueryResult<GetTenantStoresQuery>
}

export const useAccountSetUp = ({
  loggedInUser,
  auth,
  tenantStores,
}: UseAccountSetUpProps) => {
  const { state: urlState } = useUrlSearchParams<AccountSetUpParams>()
  const skipPayment = urlState?.sp === '1' || urlState?.fc === '1'
  const paymentSuccess = !!urlState?.session_id

  const userType = useMemo(() => {
    if (!loggedInUser.user?.groups?.length) return undefined
    const types: UserType[] = ['admin', 'manager', 'employee']
    return types.find(type =>
      loggedInUser.user?.groups.some(g => g.name.toLowerCase() === type),
    )
  }, [loggedInUser.user?.groups])

  const flow = useAccountSetUpFlow(userType)
  const isLoading =
    loggedInUser.loading || !auth.isInitialized || tenantStores.loading

  const isComplete = useMemo(() => {
    return (
      !!flow.length &&
      flow.every(s =>
        s.isComplete({
          authUser: auth.user,
          user: loggedInUser.user,
          skipPayment,
          paymentSuccess,
          tenantStores: tenantStores.data?.tenantStores.results || [],
        }),
      )
    )
  }, [
    flow,
    auth.user,
    loggedInUser.user,
    skipPayment,
    paymentSuccess,
    tenantStores.data?.tenantStores.results,
  ])

  return {
    flow,
    loading: isLoading,
    complete: isComplete || !!loggedInUser.user?.onboardingComplete,
    activeSubscription: loggedInUser.user?.activeSubscription || null,
    skipPayment,
    paymentSuccess,
  }
}
